import { motion } from "framer-motion";
import ScatterBackground from "@/components/ScatterBackground";

const CHAPTERS = [
  {
    num: "01",
    kicker: "the origin",
    title: "A rat grew wings.",
    body:
      "In the alley behind the Federal Reserve, a rat learned to fly. It wore a blue beanie. It carried a small bell. It said: I am a dollar. Nobody argued.",
    accent: "var(--usdc-blue)",
    align: "left",
  },
  {
    num: "02",
    kicker: "the thesis",
    title: "Stability is a vibe.",
    body:
      "Every stablecoin is a promise wrapped in a rodent. $USDC removes the wrapper and gives you the rodent directly. This is what honest money looks like — furry, winged, extremely online.",
    accent: "var(--meme-pink)",
    align: "right",
  },
  {
    num: "03",
    kicker: "the community",
    title: "CATBATHATRATFAT.",
    body:
      "Say it three times fast. Post it once. You are now a shareholder of the meme. There is no dividend. There is only the bell and the wings and the beanie and the belly.",
    accent: "var(--meme-yellow)",
    align: "left",
  },
  {
    num: "04",
    kicker: "the plan",
    title: "Buy. Hodl. Squeak.",
    body:
      "There is no roadmap because rats don't use roads. There is a launch link. Click it. The chain is ARC. The bat is fat. The rat is fatter. The hat is blue. Godspeed.",
    accent: "var(--meme-lime)",
    align: "right",
  },
];

export default function Manifesto() {
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: "var(--cream-2)" }}
      data-testid="manifesto-section"
    >
      <ScatterBackground seed={31} count={26} />

      {/* section header */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-10">
        <div className="flex items-baseline gap-4">
          <span className="font-editorial italic text-2xl md:text-3xl">
            §
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em]">
            the manifesto / four small chapters
          </span>
        </div>
        <h2 className="font-display uppercase text-[16vw] md:text-[11vw] leading-[0.85] mt-4">
          <span className="block">why a</span>
          <span
            className="block"
            style={{ color: "var(--usdc-blue)" }}
          >
            rat, exactly
          </span>
        </h2>
      </div>

      {/* chapters */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-24 space-y-24 md:space-y-40">
        {CHAPTERS.map((c, idx) => (
          <Chapter key={c.num} c={c} idx={idx} />
        ))}
      </div>
    </section>
  );
}

function Chapter({ c, idx }) {
  const isRight = c.align === "right";
  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`grid grid-cols-12 gap-6 md:gap-10 items-start ${
        isRight ? "md:pl-32" : "md:pr-32"
      }`}
      data-testid={`chapter-${c.num}`}
    >
      <div
        className={`col-span-12 md:col-span-3 ${
          isRight ? "md:order-2 md:text-right" : ""
        }`}
      >
        <div
          className="font-display text-[26vw] md:text-[10vw] leading-none"
          style={{ color: c.accent, WebkitTextStroke: "0" }}
        >
          {c.num}
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] mt-2">
          {c.kicker}
        </div>
      </div>
      <div
        className={`col-span-12 md:col-span-9 ${
          isRight ? "md:order-1" : ""
        }`}
      >
        <h3 className="font-editorial italic text-4xl md:text-6xl leading-[1.02] max-w-[22ch]">
          {c.title}
        </h3>
        <p className="mt-6 font-mono text-sm md:text-[15px] leading-relaxed max-w-[52ch] text-[color:var(--ink)]/85">
          {c.body}
        </p>

        {/* clipped photography frame */}
        {idx === 1 && (
          <ClippedPhoto seed="right" />
        )}
        {idx === 3 && (
          <ClippedPhoto seed="left" flip />
        )}
      </div>
    </motion.article>
  );
}

function ClippedPhoto({ flip }) {
  return (
    <motion.div
      initial={{ scale: 0.92, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1, delay: 0.1 }}
      className={`mt-10 relative w-full max-w-[520px] ${flip ? "ml-auto" : ""}`}
    >
      <div
        className="relative border-2 border-[color:var(--ink)] bg-[color:var(--usdc-blue)] p-6 btn-shadow"
        style={{
          clipPath: flip
            ? "polygon(0 6%, 100% 0, 96% 100%, 4% 94%)"
            : "polygon(4% 0, 100% 4%, 96% 100%, 0 96%)",
        }}
      >
        <img
          src="/img/batrat.jpg"
          alt="specimen"
          className="w-full h-auto"
          style={{ mixBlendMode: "multiply" }}
        />
        <div className="absolute top-3 left-4 font-mono text-[10px] uppercase tracking-widest text-white">
          plate {flip ? "IV" : "II"} · specimen #usdc
        </div>
      </div>
      <div
        className="absolute -bottom-3 -right-3 font-display uppercase text-lg bg-[color:var(--meme-yellow)] border-2 border-[color:var(--ink)] px-3 py-1"
        style={{ transform: "rotate(-4deg)" }}
      >
        certified fat
      </div>
    </motion.div>
  );
}
